/**
 * The agenda preview card (platform design §6.4): the next few scheduled slots, grouped by
 * account. `earliest === null` (the absent-window state, §16.4) renders a sentence about how far
 * back the ledger can see, never an empty list -- an empty agenda and "nothing recorded yet" are
 * different facts.
 */

import type { ReactNode } from 'react';

import { Link } from '@tanstack/react-router';

import { Chip } from '../../../ui/chip';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { HintedIcon } from '../../../chrome/hint';
import { getLocale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import type { UseQueryResult } from '@tanstack/react-query';

import { PlatformIcon, platformLabel } from '../accounts/NoSelection';
import { groupSlots } from './agendaGrouping';
import type { Agenda } from './api/schemas';
import type { ApiFailureError } from '../../../lib/http/errors';
import { cm } from './messages';
import { AGENDA_PATH } from './paths';
import { SlotChip } from './SlotChip';
import { WindowSelect, type WindowOption } from './WindowSelect';

const PREVIEW_SLOT_GROUPS = 2;
const PREVIEW_ACCOUNTS_PER_SLOT = 4;

function formatSlotHeader(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

export function AgendaPreviewCard(props: {
  campaignId: string;
  query: UseQueryResult<Agenda, ApiFailureError>;
  blocked: boolean;
  windowOption: WindowOption;
  onWindowChange: (option: WindowOption) => void;
}): ReactNode {
  const locale = getLocale();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <h2 className="text-title-sm">{cm('agenda.title')}</h2>
        <div className="flex items-center gap-2">
          {/* Figma 175:5249 draws the chip where 125:9 draws the window select. */}
          {props.blocked ? (
            <Chip tone="negative">{cm('agenda.noCredit')}</Chip>
          ) : (
            <WindowSelect value={props.windowOption} onChange={props.onWindowChange} />
          )}
          <Link
            to={AGENDA_PATH}
            params={{ campaignId: props.campaignId }}
            className="text-caption text-primary-ink hover:underline"
          >
            {cm('agenda.viewAll')}
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <QueryStates
          query={props.query}
          isEmpty={(agenda) => agenda.earliest !== null && agenda.slots.length === 0}
          empty={<EmptyState title={cm('agenda.empty.title')} tone="neutral" />}
        >
          {(agenda) => {
            if (agenda.earliest === null) {
              return (
                <EmptyState
                  title={cm('agenda.absentWindow.title')}
                  description={cm('agenda.absentWindow.description')}
                  tone="neutral"
                />
              );
            }

            const groups = groupSlots(agenda.slots).slice(0, PREVIEW_SLOT_GROUPS);

            return (
              <div className="flex flex-col gap-4" data-testid="agenda-slot-groups">
                {groups.map((group) => (
                  <div key={group.scheduledSlot} className="flex flex-col gap-2">
                    <p className="text-caption-sm font-medium text-muted-foreground">
                      {formatSlotHeader(group.scheduledSlot, locale)}
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {group.accounts.slice(0, PREVIEW_ACCOUNTS_PER_SLOT).map((account) => (
                        <div
                          key={account.key}
                          className="flex flex-col gap-2 rounded-lg border p-md"
                          data-testid="agenda-account-card"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="flex min-w-0 items-center gap-1.5">
                              {account.platform !== null && (
                                <HintedIcon label={platformLabel(account.platform)}>
                                  <PlatformIcon
                                    platform={account.platform}
                                    className="size-3 shrink-0"
                                  />
                                </HintedIcon>
                              )}
                              <p className="truncate text-body-sm font-medium">{account.label}</p>
                            </span>
                            {account.followerCount !== null && (
                              <span className="shrink-0 text-caption text-muted-foreground">
                                {compact(account.followerCount, locale)}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {account.jobs.map((job, index) => (
                              <SlotChip
                                key={`${job.jobType}-${index}`}
                                jobType={job.jobType}
                                state={job.state}
                                blocked={props.blocked}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          }}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
